import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePeriodo } from '../../contexts/PeriodoContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, Plus, Check, X, Loader2, Clock, AlertTriangle, 
  Calendar, User, Filter, Eye, Upload, MessageSquare, Settings,
  Users, Building, CheckSquare, Square, ListChecks
} from 'lucide-react';
import { toast } from 'sonner';

const OcorrenciasPage = () => {
  const { api, user } = useAuth();
  const { periodoSelecionado, alterarPeriodo } = usePeriodo();
  const [activeTab, setActiveTab] = useState('pendentes');
  const [ocorrencias, setOcorrencias] = useState([]);
  const [empregados, setEmpregados] = useState([]);
  const [lideres, setLideres] = useState([]);
  const [justificativasPadrao, setJustificativasPadrao] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [justificarDialogOpen, setJustificarDialogOpen] = useState(false);
  const [aprovarDialogOpen, setAprovarDialogOpen] = useState(false);
  const [liderDialogOpen, setLiderDialogOpen] = useState(false);
  const [selectedOcorrencia, setSelectedOcorrencia] = useState(null);
  
  // Estados para seleção em lote
  const [selectedIds, setSelectedIds] = useState([]);
  const [loteDialogOpen, setLoteDialogOpen] = useState(false);
  const [loteAction, setLoteAction] = useState('aprovar');
  const [loteObservacao, setLoteObservacao] = useState('');
  const [processandoLote, setProcessandoLote] = useState(false);
  
  const [filtros, setFiltros] = useState({
    tipo: '',
    empregado_id: '',
    data_inicio: '',
    data_fim: ''
  });

  // Sincronizar filtros de data com período global
  useEffect(() => {
    if (periodoSelecionado?.data_inicio && periodoSelecionado?.data_fim) {
      setFiltros(prev => ({
        ...prev,
        data_inicio: periodoSelecionado.data_inicio,
        data_fim: periodoSelecionado.data_fim
      }));
    }
  }, [periodoSelecionado]);

  // Atualizar período global quando usuário altera filtros de data
  const handleFiltroDataChange = (campo, valor) => {
    const novosFiltros = { ...filtros, [campo]: valor };
    setFiltros(novosFiltros);
    
    if (novosFiltros.data_inicio && novosFiltros.data_fim) {
      alterarPeriodo(novosFiltros.data_inicio, novosFiltros.data_fim);
    }
  };

  const [formData, setFormData] = useState({
    empregado_id: '',
    data: new Date().toISOString().split('T')[0],
    tipo: 'atraso',
    minutos: 0,
    horas: 0,
    hora_ocorrencia: '',
    empresa_id: user?.empresa_id || ''
  });

  const [justificativaData, setJustificativaData] = useState({
    justificativa_tipo: '',
    justificativa_texto: '',
    justificativa_anexo: null
  });
  
  // Manter último tipo de justificativa selecionado (persiste entre aberturas do dialog)
  const [ultimoTipoJustificativa, setUltimoTipoJustificativa] = useState('');

  const [aprovacaoData, setAprovacaoData] = useState({
    observacao: ''
  });

  const [liderData, setLiderData] = useState({
    usuario_id: '',
    setores: [],
    departamentos: [],
    empregados_ids: []
  });

  const tiposOcorrencia = [
    { value: 'atraso', label: 'Atraso', icon: '🕐', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'falta', label: 'Falta', icon: '❌', color: 'bg-red-100 text-red-800' },
    { value: 'saida_antecipada', label: 'Saída Antecipada', icon: '🚪', color: 'bg-orange-100 text-orange-800' },
    { value: 'ausencia_marcacao', label: 'Ausência de Marcação', icon: '📝', color: 'bg-purple-100 text-purple-800' },
    { value: 'entrada_antecipada', label: 'Entrada Antecipada', icon: '⏰', color: 'bg-blue-100 text-blue-800' },
    { value: 'horas_extras', label: 'Horas Extras', icon: '⏱️', color: 'bg-emerald-100 text-emerald-800' },
    { value: 'trabalho_folga', label: 'Trabalho na Folga', icon: '📅', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'trabalho_feriado', label: 'Trabalho em Feriado', icon: '🎉', color: 'bg-pink-100 text-pink-800' }
  ];

  const tiposJustificativa = [
    { value: 'compensar_banco_horas', label: 'Compensar no Banco de Horas' },
    { value: 'abono_gerencia', label: 'Abono pela Gerência' },
    { value: 'atestado_medico', label: 'Atestado Médico' },
    { value: 'declaracao_horas', label: 'Declaração de Horas' },
    { value: 'troca_turno', label: 'Troca de Turno' },
    { value: 'servico_externo', label: 'Serviço Externo' },
    { value: 'treinamento', label: 'Treinamento' },
    { value: 'reuniao', label: 'Reunião' },
    { value: 'outros', label: 'Outros' }
  ];

  useEffect(() => {
    fetchOcorrencias();
    fetchEmpregados();
    fetchLideres();
    fetchJustificativasPadrao();
  }, []);

  const fetchOcorrencias = async () => {
    try {
      let url = '/ocorrencias';
      const params = new URLSearchParams();
      if (filtros.tipo) params.append('tipo', filtros.tipo);
      if (filtros.empregado_id) params.append('empregado_id', filtros.empregado_id);
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);
      if (params.toString()) url += '?' + params.toString();
      
      const response = await api.get(url);
      setOcorrencias(response.data);
    } catch (error) {
      console.error('Error fetching ocorrencias:', error);
      toast.error('Erro ao carregar ocorrências');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpregados = async () => {
    try {
      const response = await api.get('/empregados');
      setEmpregados(response.data.filter(e => e.ativo));
    } catch (error) {
      console.error('Error fetching empregados:', error);
    }
  };

  const fetchLideres = async () => {
    try {
      const response = await api.get('/lideres');
      setLideres(response.data);
    } catch (error) {
      console.error('Error fetching lideres:', error);
    }
  };

  const fetchJustificativasPadrao = async () => {
    try {
      const response = await api.get('/justificativas-padrao');
      setJustificativasPadrao(response.data);
    } catch (error) {
      console.error('Error fetching justificativas:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/ocorrencias', formData);
      toast.success('Ocorrência registrada!');
      setDialogOpen(false);
      fetchOcorrencias();
      setFormData({
        empregado_id: '',
        data: new Date().toISOString().split('T')[0],
        tipo: 'atraso',
        minutos: 0,
        horas: 0,
        hora_ocorrencia: '',
        empresa_id: user?.empresa_id || ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar ocorrência');
    }
  };

  const handleJustificar = async () => {
    if (!selectedOcorrencia) return;
    try {
      await api.put(`/ocorrencias/${selectedOcorrencia.id}/justificar`, justificativaData);
      toast.success('Justificativa enviada! Aguardando aprovação do líder.');
      
      // Salvar último tipo de justificativa para reutilizar
      setUltimoTipoJustificativa(justificativaData.justificativa_tipo);
      
      setJustificarDialogOpen(false);
      setSelectedOcorrencia(null);
      // Não resetar o tipo, apenas o texto
      setJustificativaData(prev => ({ ...prev, justificativa_texto: '', justificativa_anexo: null }));
      fetchOcorrencias();
    } catch (error) {
      toast.error('Erro ao enviar justificativa');
    }
  };

  const handleAprovar = async () => {
    if (!selectedOcorrencia) return;
    try {
      await api.put(`/ocorrencias/${selectedOcorrencia.id}/aprovar`, aprovacaoData);
      toast.success('Ocorrência aprovada!');
      setAprovarDialogOpen(false);
      setSelectedOcorrencia(null);
      setAprovacaoData({ observacao: '' });
      fetchOcorrencias();
    } catch (error) {
      toast.error('Erro ao aprovar');
    }
  };

  const handleRejeitar = async (id) => {
    try {
      await api.put(`/ocorrencias/${id}/rejeitar`, aprovacaoData);
      toast.success('Ocorrência rejeitada!');
      fetchOcorrencias();
    } catch (error) {
      toast.error('Erro ao rejeitar');
    }
  };

  // Funções para seleção em lote
  const handleSelectAll = (lista) => {
    const pendentesComJustificativa = lista.filter(o => o.status === 'pendente' && o.justificativa_tipo);
    if (selectedIds.length === pendentesComJustificativa.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendentesComJustificativa.map(o => o.id));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleProcessarLote = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione ao menos uma ocorrência');
      return;
    }

    setProcessandoLote(true);
    try {
      const response = await api.put('/ocorrencias/lote', {
        ocorrencia_ids: selectedIds,
        acao: loteAction,
        observacao: loteObservacao || null
      });
      
      toast.success(response.data.message);
      setLoteDialogOpen(false);
      setSelectedIds([]);
      setLoteObservacao('');
      fetchOcorrencias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar em lote');
    } finally {
      setProcessandoLote(false);
    }
  };

  const handleSalvarLider = async () => {
    try {
      await api.post('/lideres', {
        ...liderData,
        empresa_id: user?.empresa_id
      });
      toast.success('Líder cadastrado!');
      setLiderDialogOpen(false);
      setLiderData({ usuario_id: '', setores: [], departamentos: [], empregados_ids: [] });
      fetchLideres();
    } catch (error) {
      toast.error('Erro ao cadastrar líder');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pendente: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      aprovada: { label: 'Aprovada', className: 'bg-green-100 text-green-800 border-green-300' },
      rejeitada: { label: 'Rejeitada', className: 'bg-red-100 text-red-800 border-red-300' },
      substituida: { label: 'Substituída', className: 'bg-slate-100 text-slate-800 border-slate-300' }
    };
    const c = config[status] || config.pendente;
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  const getTipoConfig = (tipo) => {
    return tiposOcorrencia.find(t => t.value === tipo) || { label: tipo, icon: '📌', color: 'bg-gray-100 text-gray-800' };
  };

  const getEmpregadoNome = (id) => {
    const emp = empregados.find(e => e.id === id);
    return emp ? emp.nome : 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const pendentes = ocorrencias.filter(o => o.status === 'pendente');
  const aprovadas = ocorrencias.filter(o => o.status === 'aprovada');
  const rejeitadas = ocorrencias.filter(o => o.status === 'rejeitada');
  
  // Pendentes com justificativa (elegíveis para aprovação em lote)
  const pendentesComJustificativa = pendentes.filter(o => o.justificativa_tipo);

  const renderOcorrenciasTable = (lista, showActions = true, showCheckbox = false) => (
    <Table>
      <TableHeader>
        <TableRow>
          {showCheckbox && (
            <TableHead className="w-[50px]">
              <Checkbox
                checked={pendentesComJustificativa.length > 0 && selectedIds.length === pendentesComJustificativa.length}
                onCheckedChange={() => handleSelectAll(lista)}
                data-testid="select-all-checkbox"
              />
            </TableHead>
          )}
          <TableHead>Empregado</TableHead>
          <TableHead>Data</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead>Detalhes</TableHead>
          <TableHead>Justificativa</TableHead>
          <TableHead>Status</TableHead>
          {showActions && <TableHead className="text-right">Ações</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {lista.length === 0 ? (
          <TableRow>
            <TableCell colSpan={showCheckbox ? 8 : (showActions ? 7 : 6)} className="text-center text-slate-500 py-8">
              Nenhuma ocorrência encontrada
            </TableCell>
          </TableRow>
        ) : (
          lista.map((oc) => {
            const tipoConfig = getTipoConfig(oc.tipo);
            const canSelect = oc.status === 'pendente' && oc.justificativa_tipo;
            return (
              <TableRow key={oc.id} className={selectedIds.includes(oc.id) ? 'bg-emerald-50' : ''}>
                {showCheckbox && (
                  <TableCell>
                    {canSelect ? (
                      <Checkbox
                        checked={selectedIds.includes(oc.id)}
                        onCheckedChange={() => handleSelectOne(oc.id)}
                        data-testid={`checkbox-${oc.id}`}
                      />
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </TableCell>
                )}
                <TableCell className="font-medium">{getEmpregadoNome(oc.empregado_id)}</TableCell>
                <TableCell>{new Date(oc.data).toLocaleDateString('pt-BR')}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className={tipoConfig.color}>
                    {tipoConfig.icon} {tipoConfig.label}
                  </Badge>
                </TableCell>
                <TableCell>
                  {oc.minutos > 0 && <span className="text-sm">{oc.minutos} min</span>}
                  {oc.horas > 0 && <span className="text-sm">{oc.horas?.toFixed(1)}h</span>}
                  {oc.hora_ocorrencia && <span className="text-xs text-slate-500 ml-2">({oc.hora_ocorrencia})</span>}
                </TableCell>
                <TableCell>
                  {oc.justificativa_tipo ? (
                    <div className="space-y-1">
                      <Badge variant="outline" className="text-xs">
                        {tiposJustificativa.find(j => j.value === oc.justificativa_tipo)?.label || oc.justificativa_tipo}
                      </Badge>
                      {oc.justificativa_texto && (
                        <p className="text-xs text-slate-500 truncate max-w-[150px]" title={oc.justificativa_texto}>
                          {oc.justificativa_texto}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-400 text-sm">Sem justificativa</span>
                  )}
                </TableCell>
                <TableCell>{getStatusBadge(oc.status)}</TableCell>
                {showActions && (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {oc.status === 'pendente' && !oc.justificativa_tipo && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedOcorrencia(oc);
                            // Manter último tipo de justificativa selecionado
                            setJustificativaData(prev => ({
                              ...prev,
                              justificativa_tipo: ultimoTipoJustificativa || prev.justificativa_tipo,
                              justificativa_texto: ''
                            }));
                            setJustificarDialogOpen(true);
                          }}
                          data-testid={`btn-justificar-${oc.id}`}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Justificar
                        </Button>
                      )}
                      {oc.status === 'pendente' && oc.justificativa_tipo && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedOcorrencia(oc);
                              setAprovarDialogOpen(true);
                            }}
                            data-testid={`btn-aprovar-${oc.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejeitar(oc.id)}
                            data-testid={`btn-rejeitar-${oc.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ocorrencias-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-1">Gestão de Ocorrências</h2>
          <p className="text-slate-600">Gerencie justificativas de atrasos, faltas, horas extras e outros</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={liderDialogOpen} onOpenChange={setLiderDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="btn-gerenciar-lideres">
                <Users className="w-4 h-4 mr-2" />
                Líderes
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">Gerenciar Líderes</DialogTitle>
                <DialogDescription>Configure quem pode aprovar ocorrências</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Usuário Líder</Label>
                  <Select
                    value={liderData.usuario_id}
                    onValueChange={(value) => setLiderData({...liderData, usuario_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um usuário" />
                    </SelectTrigger>
                    <SelectContent>
                      {empregados.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Setor (opcional)</Label>
                  <Input
                    placeholder="Ex: Administrativo, Operacional"
                    value={liderData.setores.join(', ')}
                    onChange={(e) => setLiderData({...liderData, setores: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                  />
                  <p className="text-xs text-slate-500">Separe múltiplos setores por vírgula</p>
                </div>
                <div className="space-y-2">
                  <Label>Departamento (opcional)</Label>
                  <Input
                    placeholder="Ex: RH, Financeiro"
                    value={liderData.departamentos.join(', ')}
                    onChange={(e) => setLiderData({...liderData, departamentos: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setLiderDialogOpen(false)}>Cancelar</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSalvarLider}>
                  Salvar Líder
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="nova-ocorrencia-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nova Ocorrência
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">Registrar Ocorrência</DialogTitle>
                <DialogDescription>Registre uma ocorrência manualmente</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Empregado *</Label>
                  <Select
                    value={formData.empregado_id}
                    onValueChange={(value) => setFormData({...formData, empregado_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o empregado" />
                    </SelectTrigger>
                    <SelectContent>
                      {empregados.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.matricula} - {emp.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data *</Label>
                    <Input
                      type="date"
                      value={formData.data}
                      onChange={(e) => setFormData({...formData, data: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo *</Label>
                    <Select
                      value={formData.tipo}
                      onValueChange={(value) => setFormData({...formData, tipo: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {tiposOcorrencia.map(tipo => (
                          <SelectItem key={tipo.value} value={tipo.value}>
                            {tipo.icon} {tipo.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Minutos</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.minutos}
                      onChange={(e) => setFormData({...formData, minutos: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Horas</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.horas}
                      onChange={(e) => setFormData({...formData, horas: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Horário</Label>
                    <Input
                      type="time"
                      value={formData.hora_ocorrencia}
                      onChange={(e) => setFormData({...formData, hora_ocorrencia: e.target.value})}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Registrar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-900">{pendentes.length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Aprovadas</p>
                <p className="text-2xl font-bold text-green-900">{aprovadas.length}</p>
              </div>
              <Check className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700">Rejeitadas</p>
                <p className="text-2xl font-bold text-red-900">{rejeitadas.length}</p>
              </div>
              <X className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Líderes</p>
                <p className="text-2xl font-bold text-slate-900">{lideres.length}</p>
              </div>
              <Users className="w-8 h-8 text-slate-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2 min-w-[180px]">
              <Label>Tipo</Label>
              <Select
                value={filtros.tipo}
                onValueChange={(value) => setFiltros({...filtros, tipo: value === 'todos' ? '' : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os tipos</SelectItem>
                  {tiposOcorrencia.map(tipo => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.icon} {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 min-w-[200px]">
              <Label>Empregado</Label>
              <Select
                value={filtros.empregado_id}
                onValueChange={(value) => setFiltros({...filtros, empregado_id: value === 'todos' ? '' : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os empregados</SelectItem>
                  {empregados.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Data Início</Label>
              <Input
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => handleFiltroDataChange('data_inicio', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={filtros.data_fim}
                onChange={(e) => handleFiltroDataChange('data_fim', e.target.value)}
              />
            </div>
            <Button onClick={fetchOcorrencias} variant="outline">
              <Filter className="w-4 h-4 mr-2" />
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="pendentes" className="data-[state=active]:bg-yellow-100">
            Pendentes ({pendentes.length})
          </TabsTrigger>
          <TabsTrigger value="aprovadas" className="data-[state=active]:bg-green-100">
            Aprovadas ({aprovadas.length})
          </TabsTrigger>
          <TabsTrigger value="rejeitadas" className="data-[state=active]:bg-red-100">
            Rejeitadas ({rejeitadas.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendentes" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {/* Barra de ações em lote */}
              {pendentesComJustificativa.length > 0 && (
                <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <ListChecks className="w-5 h-5 text-slate-600" />
                    <span className="text-sm text-slate-600">
                      {selectedIds.length > 0 
                        ? `${selectedIds.length} ocorrência(s) selecionada(s)`
                        : `${pendentesComJustificativa.length} justificadas aguardando aprovação`
                      }
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {selectedIds.length > 0 && (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => {
                            setLoteAction('aprovar');
                            setLoteDialogOpen(true);
                          }}
                          data-testid="btn-aprovar-lote"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Aprovar Selecionadas
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setLoteAction('rejeitar');
                            setLoteDialogOpen(true);
                          }}
                          data-testid="btn-rejeitar-lote"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Rejeitar Selecionadas
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedIds([])}
                          data-testid="btn-limpar-selecao"
                        >
                          Limpar Seleção
                        </Button>
                      </>
                    )}
                    {selectedIds.length === 0 && pendentesComJustificativa.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSelectAll(pendentes)}
                        data-testid="btn-selecionar-todas"
                      >
                        <CheckSquare className="w-4 h-4 mr-1" />
                        Selecionar Todas
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {renderOcorrenciasTable(pendentes, true, true)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aprovadas" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {renderOcorrenciasTable(aprovadas, false)}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rejeitadas" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {renderOcorrenciasTable(rejeitadas, false)}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Justificar */}
      <Dialog open={justificarDialogOpen} onOpenChange={setJustificarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Enviar Justificativa</DialogTitle>
            <DialogDescription>
              Justificativa para: {selectedOcorrencia && getTipoConfig(selectedOcorrencia.tipo).label}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Justificativa *</Label>
              <Select
                value={justificativaData.justificativa_tipo}
                onValueChange={(value) => setJustificativaData({...justificativaData, justificativa_tipo: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a justificativa" />
                </SelectTrigger>
                <SelectContent>
                  {tiposJustificativa.map(j => (
                    <SelectItem key={j.value} value={j.value}>
                      {j.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Texto Complementar</Label>
              <Textarea
                placeholder="Descreva detalhes adicionais..."
                value={justificativaData.justificativa_texto}
                onChange={(e) => setJustificativaData({...justificativaData, justificativa_texto: e.target.value})}
                rows={3}
              />
            </div>
            {justificativaData.justificativa_tipo === 'compensar_banco_horas' && (
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-800">
                  <strong>Nota:</strong> Ao aprovar, as horas desta ocorrência serão compensadas no banco de horas.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJustificarDialogOpen(false)}>Cancelar</Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700" 
              onClick={handleJustificar}
              disabled={!justificativaData.justificativa_tipo}
            >
              Enviar Justificativa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Aprovar */}
      <Dialog open={aprovarDialogOpen} onOpenChange={setAprovarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Aprovar Ocorrência</DialogTitle>
            <DialogDescription>
              Confirme a aprovação da justificativa
            </DialogDescription>
          </DialogHeader>
          {selectedOcorrencia && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <p><strong>Empregado:</strong> {getEmpregadoNome(selectedOcorrencia.empregado_id)}</p>
                <p><strong>Tipo:</strong> {getTipoConfig(selectedOcorrencia.tipo).label}</p>
                <p><strong>Justificativa:</strong> {tiposJustificativa.find(j => j.value === selectedOcorrencia.justificativa_tipo)?.label}</p>
                {selectedOcorrencia.justificativa_texto && (
                  <p><strong>Detalhes:</strong> {selectedOcorrencia.justificativa_texto}</p>
                )}
              </div>
              {selectedOcorrencia.compensar_banco && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Atenção:</strong> Esta aprovação irá compensar {selectedOcorrencia.horas?.toFixed(1) || 0}h no banco de horas.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Observação (opcional)</Label>
                <Textarea
                  placeholder="Adicione uma observação..."
                  value={aprovacaoData.observacao}
                  onChange={(e) => setAprovacaoData({...aprovacaoData, observacao: e.target.value})}
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAprovarDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleAprovar}>
              <Check className="w-4 h-4 mr-2" />
              Aprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Ação em Lote */}
      <Dialog open={loteDialogOpen} onOpenChange={setLoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {loteAction === 'aprovar' ? 'Aprovar em Lote' : 'Rejeitar em Lote'}
            </DialogTitle>
            <DialogDescription>
              {loteAction === 'aprovar' 
                ? `Confirme a aprovação de ${selectedIds.length} ocorrência(s)`
                : `Confirme a rejeição de ${selectedIds.length} ocorrência(s)`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${loteAction === 'aprovar' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-sm ${loteAction === 'aprovar' ? 'text-green-800' : 'text-red-800'}`}>
                <strong>{selectedIds.length}</strong> ocorrência(s) justificada(s) serão {loteAction === 'aprovar' ? 'aprovadas' : 'rejeitadas'}.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Observação para todas (opcional)</Label>
              <Textarea
                placeholder="Adicione uma observação que será aplicada a todas..."
                value={loteObservacao}
                onChange={(e) => setLoteObservacao(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoteDialogOpen(false)} disabled={processandoLote}>
              Cancelar
            </Button>
            <Button 
              className={loteAction === 'aprovar' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={handleProcessarLote}
              disabled={processandoLote}
              data-testid="btn-confirmar-lote"
            >
              {processandoLote ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  {loteAction === 'aprovar' ? <Check className="w-4 h-4 mr-2" /> : <X className="w-4 h-4 mr-2" />}
                  Confirmar {loteAction === 'aprovar' ? 'Aprovação' : 'Rejeição'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OcorrenciasPage;
